/**
 * Socket.IO client for real-time chat updates.
 * Handles 'last_message_updated' events from the backend.
 */

import { io } from 'socket.io-client';
import { getStoredUser, getToken } from './auth';
import { getBaseUrl } from './utils';
let socketInstance = null;

function getCompanyId() {
  const user = getStoredUser();
  return user?.company_id ?? null;
}

/**
 * Get or create socket instance
 * @param {number} companyId - Company ID to filter socket events (resolved from session by default)
 * @returns {Socket} Socket.io instance
 */
export function getSocket(companyId) {
  if (companyId == null) companyId = getCompanyId();
  // Return existing instance even if disconnected — Socket.IO handles
  // reconnection automatically and all listeners stay attached.
  if (socketInstance) {
    return socketInstance;
  }

  const baseUrl = getBaseUrl();
  socketInstance = io(baseUrl, {
    path: '/bot/socket.io',
    // The server puts this socket in its company's room from the session
    // token. A function so every reconnect sends the current token.
    auth: (cb) => cb({ token: getToken() }),
    transports: ['websocket'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
  });

  socketInstance.on('connect', () => {
    console.log('[Socket] Connected to server');
  });

  socketInstance.on('disconnect', () => {
    console.log('[Socket] Disconnected from server');
  });

  socketInstance.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error);
  });

  return socketInstance;
}

/**
 * Disconnect socket instance
 */
export function disconnectSocket() {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
}

/**
 * Subscribe to 'last_message_updated' events
 * @param {Function} callback - Callback function to handle events
 * @param {number} companyId - Company ID to filter events (default: 9)
 * @returns {Function} Unsubscribe function
 */
export function subscribeToLastMessageUpdates(callback, companyId) {
  if (companyId == null) companyId = getCompanyId();
  const socket = getSocket(companyId);
  
  const handler = (payload) => {
    // Filter by company_id/admin_id
    if (payload.admin_id !== companyId) {
      console.log('[Socket] Ignoring event - company_id mismatch:', payload.admin_id, 'expected:', companyId);
      return;
    }
    
    callback(payload);
  };

  socket.on('last_message_updated', handler);

  // Return unsubscribe function
  return () => {
    socket.off('last_message_updated', handler);
  };
}

/**
 * Subscribe to 'human_support_alert' events (customer requested human support).
 * @param {Function} callback - Callback function with payload { admin_id, contact_id }
 * @param {number} companyId - Company/admin ID to filter events (same as last_message_updated)
 * @returns {Function} Unsubscribe function
 */
export function subscribeToHumanSupportAlert(callback, companyId) {
  if (companyId == null) companyId = getCompanyId();
  const socket = getSocket(companyId);

  const handler = (payload) => {
    if (payload.admin_id !== companyId) return;
    callback(payload);
  };

  socket.on('human_support_alert', handler);
  return () => {
    socket.off('human_support_alert', handler);
  };
}

/**
 * Subscribe to 'member_assigned' events (a conversation was assigned to a member).
 * Only fires callback when admin_id matches company_id AND member_id matches the
 * logged-in user's id.
 * @param {Function} callback - Callback with payload { admin_id, contact_id, member_id, message }
 * @param {number} companyId - Company/admin ID to filter events
 * @returns {Function} Unsubscribe function
 */
export function subscribeToMemberAssigned(callback, companyId) {
  if (companyId == null) companyId = getCompanyId();
  const socket = getSocket(companyId);

  console.log('[Socket] Subscribing to member_assigned, socket connected:', socket.connected, 'companyId:', companyId);

  const handler = (payload) => {
    console.log('[Socket] member_assigned event received:', payload);
    const user = getStoredUser();
    if (!user) {
      console.log('[Socket] No stored user, ignoring member_assigned');
      return;
    }
    // Use loose comparison (==) to handle number vs string mismatches
    if (String(payload.admin_id) !== String(companyId)) {
      console.log('[Socket] member_assigned admin_id mismatch:', payload.admin_id, 'expected:', companyId);
      return;
    }
    if (String(payload.member_id) !== String(user.id)) {
      console.log('[Socket] member_assigned member_id mismatch:', payload.member_id, 'expected:', user.id);
      return;
    }
    console.log('[Socket] member_assigned matched! Firing callback');
    callback(payload);
  };

  socket.on('member_assigned', handler);

  // Also log when this socket connects/reconnects so we know it's alive
  socket.on('connect', () => {
    console.log('[Socket] Bot socket connected (member_assigned listener active)');
  });

  return () => {
    socket.off('member_assigned', handler);
  };
}
